import { computeDynamicBillSummaryNodeAmount, computeDynamicBillSummaryNodeRate, combineResourceCodes } from "./dynamicBillSummarySerialization";
import type {
  DynamicBillSummaryNode,
  DynamicBillSummaryItemNode,
  DynamicBillSummaryTab,
} from "../types/tender.types";
import type { PlotMatrixCategoryColumn, PlotMatrixRow } from "./plotMatrixCategoryGenerator";

/**
 * Collapses a column's `categoryName` down to its Valuation Stage — same rule
 * PlotMatrix.tsx's own Stage View uses (the "─── Valuation Stage View ───"
 * `useMemo`): strip everything from the last " - " onward, e.g.
 * "Foundation Exc & Conc - Abnormals" -> "Foundation Exc & Conc". A category
 * with no " - " suffix is already its own stage.
 */
export const collapsePlotMatrixStageName = (categoryName: string): string => {
  const lastDashIdx = categoryName.lastIndexOf(" - ");
  return lastDashIdx !== -1 ? categoryName.slice(0, lastDashIdx) : categoryName;
};

/**
 * Every Valuation Stage that actually exists right now, in the exact order
 * Plot Matrix's own Stage View lists them — derived from the live columns
 * (`generatePlotMatrixCategoryColumns`), not a static category map, so the
 * lookup can never offer a stage with no real underlying column(s).
 */
export const getPlotMatrixStageNames = (columns: PlotMatrixCategoryColumn[]): string[] => {
  const seen = new Set<string>();
  const order: string[] = [];
  columns.forEach((column) => {
    const stage = collapsePlotMatrixStageName(column.categoryName);
    if (!seen.has(stage)) {
      seen.add(stage);
      order.push(stage);
    }
  });
  return order;
};

/** Every column that collapses to the given Valuation Stage. */
export const findPlotMatrixColumnsForStage = (
  columns: PlotMatrixCategoryColumn[],
  stage: string,
): PlotMatrixCategoryColumn[] => columns.filter((c) => collapsePlotMatrixStageName(c.categoryName) === stage);

/**
 * Valuation Stages whose Add/Edit Item modal "Distribute amount across" may
 * offer "Garages" — every other stage (Plot Drainage, Service Connections,
 * Plot Drives, Plot Pavings, Garden, Ret & Screen Walls, etc.) is plot-only.
 * Checked in addition to `hasGarages` (whether the tender has any garage-type
 * take-off rows at all) by `DynamicBillSummaryPanel.tsx` and
 * `useBillSummaryExtraItems.tsx` — both conditions must hold for the
 * "Garages" checkbox to show.
 */
export const GARAGE_ELIGIBLE_VALUATION_STAGES = new Set<string>([
  "Site Strip",
  "Cut/Fill",
  "Pile Matts",
  "Foundation Exc & Conc",
  "Foundations to DPC",
  "Foundation Floor",
  "Scaffold Platforms",
]);

/** Whether the given Valuation Stage is one where "Garages" may be offered — see `GARAGE_ELIGIBLE_VALUATION_STAGES`. */
export const isGarageEligibleValuationStage = (stage: string | undefined): boolean =>
  !!stage && GARAGE_ELIGIBLE_VALUATION_STAGES.has(stage);

/**
 * The Stage (sub-stage) label for one column within its Valuation Stage —
 * the part of `categoryName` after the last " - " (e.g. "Excavate
 * Foundation" out of "Foundation Exc & Conc - Excavate Foundation"). A
 * column with no " - " suffix has no separate sub-stage — the stage IS the
 * whole categoryName, so the label is just that name.
 */
export const getPlotMatrixSubStageLabel = (categoryName: string): string => {
  const lastDashIdx = categoryName.lastIndexOf(" - ");
  return lastDashIdx !== -1 ? categoryName.slice(lastDashIdx + 3) : categoryName;
};

const isSectionNode = (
  node: DynamicBillSummaryNode,
): node is Extract<DynamicBillSummaryNode, { type: "section" }> => node.type === "section";

const collectMappedItems = (nodes: DynamicBillSummaryNode[], out: DynamicBillSummaryItemNode[]) => {
  nodes.forEach((node) => {
    if (isSectionNode(node)) {
      collectMappedItems(node.children, out);
    } else if (node.plotMapping) {
      out.push(node);
    }
  });
};

/**
 * Additive overlay applied AFTER `generatePlotMatrixCategoryRows` returns —
 * Plot Matrix's own row/column generation is never modified by this
 * function. When no item carries a `plotMapping`, it returns `rows`
 * unchanged (same array reference), so Plot Matrix behaves exactly as it did
 * before this feature existed.
 *
 * For each mapped item: resolves its exact Stage selection (`mapping.categoryName`)
 * to its real column — deterministic, no guessing, since the modal's Stage
 * dropdown only ever offers real `categoryName` values in the first place —
 * computes the item's amount, and splits it equally across every selected
 * plot/garage row (both types combined into one pool when both are
 * selected — not divided per-type first), adding the share onto that
 * column's cell for each target row. Column/grand totals are computed by
 * summing that same field at render time (PlotMatrix.tsx), so they pick up
 * the addition automatically.
 */
export function applyDynamicBillSummaryToPlotMatrixRows(
  rows: PlotMatrixRow[],
  columns: PlotMatrixCategoryColumn[],
  dynamicTabs: DynamicBillSummaryTab[],
  /** OH&P/Preliminaries markup — `(1 + overheadAndProfit/100) * (1 + preliminaries/100)`,
   *  same formula every fixed Plot Matrix cell already has applied. Defaults
   *  to 1 (no markup) when omitted. */
  markupFactor: number = 1,
  /** Ad-hoc extra-item bucket sets that can also carry a `plotMapping` — e.g.
   *  `state.groundworks.cutFillsExtraItems`, `state.groundworks.foundationsExtraItems`
   *  (useBillSummaryExtraItems.tsx, one independent bucket set per fixed Bill
   *  Summary). Each bucket set's own attachment-point keys (e.g. `"__root__"`)
   *  are local to that fixed summary, so they're kept as separate records
   *  here rather than merged into one — merging would silently collide on
   *  identical attachment keys from different summaries. Each bucket's node
   *  tree is walked the same way a tab's `nodes` are. */
  ...extraItemBucketSets: Array<Record<string, DynamicBillSummaryNode[]> | undefined>
): PlotMatrixRow[] {

  const mappedItems: DynamicBillSummaryItemNode[] = [];
  dynamicTabs.forEach((tab) => collectMappedItems(tab.nodes, mappedItems));
  extraItemBucketSets.forEach((extraItemBuckets) => {
    if (extraItemBuckets) {
      Object.values(extraItemBuckets).forEach((nodes) => collectMappedItems(nodes, mappedItems));
    }
  });
  if (mappedItems.length === 0) return rows;

  const mutableRows = rows.map((row) => ({ ...row }));

  mappedItems.forEach((item) => {
    const mapping = item.plotMapping;
    if (!mapping) return;
    const column = columns.find((c) => c.categoryName === mapping.categoryName);
    if (!column) return;

    // Marked up (OH&P/Preliminaries) the same way every fixed Plot Matrix
    // cell already is, so a mapped dynamic item's share reconciles with the
    // marked-up Bill Summary/Sum Analysis total it's drawn from.
    const amount = computeDynamicBillSummaryNodeAmount(item, markupFactor);
    if (!amount) return;

    const targets = mutableRows.filter((row) => {
      if (row.rowType === "plot" && mapping.distributeTo.includes("plots")) {
        return !mapping.plotIds?.length || mapping.plotIds.includes(row.plotNo);
      }
      if (row.rowType === "garage" && mapping.distributeTo.includes("garages")) {
        return !mapping.garageIds?.length || mapping.garageIds.includes(row.plotNo);
      }
      return false;
    });
    if (targets.length === 0) return;

    // Every Plot Matrix cell is an object (`{ total, _breakdown? }`), never a
    // bare number — `calculateRowTotal`/the column-totals strip both sum
    // `cell.total` specifically (plotMatrixCategoryGenerator.ts). Bump
    // `.total` in place (preserving any `_breakdown`) rather than replacing
    // the cell with a plain number, which would silently zero it out.
    const share = amount / targets.length;
    targets.forEach((row) => {
      const existingCell = row[column.dataIndex] as { total?: number; [key: string]: unknown } | undefined;
      const currentTotal = typeof existingCell?.total === "number" ? existingCell.total : 0;
      row[column.dataIndex] = { ...existingCell, total: currentTotal + share };
    });
  });

  return mutableRows;
}

/**
 * Register-payload resource entries for the plot-mapped items that
 * `applyDynamicBillSummaryToPlotMatrixRows` folds into this row's cell for
 * `columnCategoryName` — same item collection and target-row rule. They are
 * appended to that column's existing task `resource[]`, so the items behind
 * the Plot Matrix number are sent as resources, not just inside the total.
 * Each entry is the item's code with its qty split equally across the target
 * rows (`multiplier` = OH&P/Preliminaries uplift, like every other resource
 * entry). Items with no code or qty yield no entry.
 */
export function getDynamicBillSummaryResourcesForCell(
  row: PlotMatrixRow | undefined,
  allRows: PlotMatrixRow[],
  columnCategoryName: string,
  dynamicTabs: DynamicBillSummaryTab[],
  upliftFactor: number,
  /** Item-library nettRate by upper-cased resource code. The backend prices a
   *  resource as `qty × libraryRate(code)`, so `multiplier` is solved to make
   *  that equal the item's own marked-up rate (see below). */
  rateByCode: Map<string, number>,
  ...extraItemBucketSets: Array<Record<string, DynamicBillSummaryNode[]> | undefined>
): { code: string; actualQty: number; qty: number; multiplier: number }[] {
  if (!row) return [];
  const mappedItems: DynamicBillSummaryItemNode[] = [];
  dynamicTabs.forEach((tab) => collectMappedItems(tab.nodes, mappedItems));
  extraItemBucketSets.forEach((buckets) => {
    if (buckets) Object.values(buckets).forEach((nodes) => collectMappedItems(nodes, mappedItems));
  });
  const out: { code: string; actualQty: number; qty: number; multiplier: number }[] = [];
  mappedItems.forEach((item) => {
    const mapping = item.plotMapping;
    if (!mapping || mapping.categoryName !== columnCategoryName) return;
    const isTarget = (r: PlotMatrixRow): boolean =>
      (r.rowType === "plot" &&
        mapping.distributeTo.includes("plots") &&
        (!mapping.plotIds?.length || mapping.plotIds.includes(r.plotNo))) ||
      (r.rowType === "garage" &&
        mapping.distributeTo.includes("garages") &&
        (!mapping.garageIds?.length || mapping.garageIds.includes(r.plotNo)));
    if (!isTarget(row)) return;
    const code = String(combineResourceCodes(item) ?? "").trim();
    const actualQty = Math.abs(item.qty ?? 0) / (allRows.filter(isTarget).length || 1);
    if (!code || !actualQty) return;
    // Library rate for the code; a combined code ("A+B-C") is the signed sum,
    // same operators the item's own combined rate used.
    const libraryRate = (code.match(/[+-]?[^+-]+/g) ?? []).reduce((sum, part) => {
      const sign = part.startsWith("-") ? -1 : 1;
      return sum + sign * (rateByCode.get(part.replace(/^[+-]/, "").trim().toUpperCase()) ?? 0);
    }, 0);
    // Target cost per row = qty share × the item's rounded, marked-up rate —
    // exactly what the Bill Summary/Plot Matrix amount uses. Solve the
    // multiplier so `qty(=actualQty × multiplier) × libraryRate` reproduces
    // it even when the stored rate has drifted from the library rate or was
    // rounded to 2dp; fall back to the plain uplift when either rate is 0.
    const markedUpRate = computeDynamicBillSummaryNodeRate(item.rate, upliftFactor);
    const multiplier = libraryRate > 0 && markedUpRate > 0 ? markedUpRate / libraryRate : upliftFactor;
    out.push({ code, actualQty, qty: actualQty * multiplier, multiplier });
  });
  return out;
}
